import type { useApolloClient } from '@apollo/client/react'
import type {
  CommunicationNotificationsQuery,
  CommunicationUnreadTotalQuery,
} from '@/graphql/queries/communicationNotificationsQuery.gql.generated'
import {
  CommunicationNotificationsDocument,
  CommunicationUnreadTotalDocument,
} from '@/graphql/queries/communicationNotificationsQuery.gql.generated'

export type Workspace = { id: string; name: string }
export type NotificationEntry =
  CommunicationNotificationsQuery['communicationNotifications'][number] & {
    workspaceName: string
  }
export type NotificationFeed = {
  total: number
  entries: Array<NotificationEntry>
}
type Client = ReturnType<typeof useApolloClient>

async function loadWorkspaceEntries(
  client: Client,
  workspace: Workspace,
): Promise<Array<NotificationEntry>> {
  const entries: Array<NotificationEntry> = []
  let after: string | undefined
  let page: CommunicationNotificationsQuery['communicationNotifications']
  do {
    const result = await client.query<CommunicationNotificationsQuery>({
      query: CommunicationNotificationsDocument,
      variables: { input: { workspaceId: workspace.id, first: 100, after } },
      fetchPolicy: 'network-only',
    })
    page = result.data?.communicationNotifications ?? []
    entries.push(
      ...page.map((entry) => ({ ...entry, workspaceName: workspace.name })),
    )
    after = page.at(-1)?.id
  } while (page.length === 100)

  return entries
}

export async function loadNotificationFeed(
  client: Client,
  workspaces: Array<Workspace>,
  workspaceId: string,
  canRead: boolean,
): Promise<NotificationFeed> {
  let total = 0
  if (canRead) {
    try {
      const result = await client.query<CommunicationUnreadTotalQuery>({
        query: CommunicationUnreadTotalDocument,
        variables: { workspaceId },
        fetchPolicy: 'network-only',
      })
      total = result.data?.communicationUnreadTotal ?? 0
    } catch {
      total = 0
    }
  }
  const entries: Array<NotificationEntry> = []
  for (const workspace of workspaces) {
    try {
      entries.push(...(await loadWorkspaceEntries(client, workspace)))
    } catch {
      /* Workspace access and feature decisions are independent. */
    }
  }
  entries.sort(
    (a, b) =>
      new Date(b.updatedAt as string).getTime() -
      new Date(a.updatedAt as string).getTime(),
  )

  return { total, entries }
}

export function createNotificationRefresh(
  load: () => Promise<NotificationFeed>,
  receive: (feed: NotificationFeed) => void,
) {
  const state = { active: true, running: false, requested: 0 }
  const receiveIfActive = (feed: NotificationFeed) => {
    if (state.active) receive(feed)
  }
  const refresh = async () => {
    state.requested += 1
    if (state.running) return
    state.running = true
    let delivered = 0
    try {
      while (state.active && delivered < state.requested) {
        delivered = state.requested
        const feed = await load()
        receiveIfActive(feed)
      }
    } finally {
      state.running = false
    }
  }

  return {
    refresh,
    dispose: () => {
      state.active = false
    },
  }
}
